import { apiClient } from './apiClient';

export interface PresignedUploadRequest {
  fileName: string;
  contentType: string;
  folder?: string;
  expirationMinutes?: number;
  fileSizeBytes?: number;
}

export interface PresignedUploadResponse {
  uploadUrl: string;
  s3Key: string;
  publicUrl: string;
  formFields: Record<string, string>;
  expiresAt: string;
}

export interface UploadConfirmationRequest {
  s3Key: string;
}

export interface UploadConfirmationResponse {
  s3Key: string;
  downloadUrl: string;
  success: boolean;
  message: string;
}

export interface S3UploadResult {
  success: boolean;
  /**
   * The permanent S3 object key. This is the value that must be stored in the
   * database — it never expires. Use this as the authoritative image identifier.
   */
  s3Key?: string;
  publicUrl?: string;
  /**
   * A short-lived presigned download URL (60 min). Use only for immediate
   * display after upload. Do NOT persist this value to the database.
   */
  downloadUrl?: string;
  /**
   * Alias for s3Key — provided for callers that previously stored a "url" field.
   * Guaranteed to be the same value as s3Key when present.
   * @deprecated Prefer s3Key explicitly to make intent clear.
   */
  url?: string;
  error?: string;
}

/**
 * Service for handling S3 presigned URL uploads
 * This bypasses CloudFront's 20MB request body limitation
 */
export class S3UploadService {
  /**
   * Upload a file directly to S3 using presigned URLs
   * @param file The file to upload
   * @param folder Optional folder/prefix for the S3 object
   * @returns Upload result with S3 key and URLs
   */
  async uploadFile(file: File, folder: string = 'reports'): Promise<S3UploadResult> {
    try {
      console.log(`Starting S3 upload for file: ${file.name}, size: ${Math.round(file.size / 1024)}KB`);

      // Step 1: Get presigned URL from our API
      const presignedRequest: PresignedUploadRequest = {
        fileName: file.name,
        contentType: file.type,
        folder: folder,
        expirationMinutes: 15,
        fileSizeBytes: file.size
      };

      const presignedResponse = await apiClient.post<PresignedUploadResponse>(
        '/imageupload/presigned-url',
        presignedRequest
      );

      if (!presignedResponse.success || !presignedResponse.data) {
        throw new Error(presignedResponse.message || 'Failed to get presigned URL');
      }

      const { uploadUrl, s3Key, publicUrl } = presignedResponse.data;
      console.log(`Got presigned URL for S3 key: ${s3Key}`);

      // Step 2: Upload directly to S3 using the presigned URL
      const uploadResult = await this.uploadToS3(uploadUrl, file, presignedResponse.data.formFields);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload to S3');
      }

      console.log(`Successfully uploaded to S3: ${s3Key}`);

      // Step 3: Confirm upload with our API to get download URL
      const confirmationRequest: UploadConfirmationRequest = {
        s3Key: s3Key
      };

      const confirmationResponse = await apiClient.post<UploadConfirmationResponse>(
        '/imageupload/confirm-upload',
        confirmationRequest
      );

      if (!confirmationResponse.success || !confirmationResponse.data) {
        console.warn('Failed to confirm upload, but file was uploaded successfully');
        // Even if confirmation fails, the upload was successful.
        // Return s3Key as both s3Key and url — callers must store s3Key, not downloadUrl.
        return {
          success: true,
          s3Key: s3Key,
          url: s3Key,
          publicUrl: publicUrl,
          downloadUrl: publicUrl
        };
      }

      console.log(`Upload confirmed successfully: ${s3Key}`);

      // s3Key is the permanent identifier to store in the DB.
      // downloadUrl is a short-lived presigned URL for immediate display only.
      return {
        success: true,
        s3Key: s3Key,
        url: s3Key,
        publicUrl: publicUrl,
        downloadUrl: confirmationResponse.data.downloadUrl
      };

    } catch (error: any) {
      console.error('S3 upload failed:', error);
      return {
        success: false,
        error: error.message || 'Upload failed'
      };
    }
  }

  /**
   * Upload a base64 image to S3 by converting it to a File first
   * @param base64Image The base64 image string
   * @param fileName The desired file name
   * @param folder Optional folder/prefix for the S3 object
   * @returns Upload result with S3 key and URLs
   */
  async uploadBase64Image(
    base64Image: string, 
    fileName: string = 'image.jpg', 
    folder: string = 'reports'
  ): Promise<S3UploadResult> {
    try {
      // Convert base64 to File
      const file = this.base64ToFile(base64Image, fileName);
      
      // Upload the file
      return await this.uploadFile(file, folder);
    } catch (error: any) {
      console.error('Failed to upload base64 image:', error);
      return {
        success: false,
        error: error.message || 'Failed to convert and upload base64 image'
      };
    }
  }

  /**
   * Upload multiple files to S3
   * @param files Array of files to upload
   * @param folder Optional folder/prefix for the S3 objects
   * @returns Array of upload results
   */
  async uploadMultipleFiles(files: File[], folder: string = 'reports'): Promise<S3UploadResult[]> {
    const results: S3UploadResult[] = [];
    
    // Upload files sequentially to avoid overwhelming the API
    for (const file of files) {
      const result = await this.uploadFile(file, folder);
      results.push(result);
      
      // Add a small delay between uploads
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return results;
  }

  /**
   * Get a download URL for an S3 object
   * @param s3Key The S3 object key
   * @param expirationMinutes How long the URL should be valid
   * @returns Download URL
   */
  async getDownloadUrl(s3Key: string, expirationMinutes: number = 60): Promise<string | null> {
    try {
      const response = await apiClient.get<{ downloadUrl: string }>(
        `/imageupload/download-url?s3Key=${encodeURIComponent(s3Key)}&expirationMinutes=${expirationMinutes}`
      );

      if (response.success && response.data) {
        return response.data.downloadUrl;
      }

      return null;
    } catch (error) {
      console.error('Failed to get download URL:', error);
      return null;
    }
  }

  /**
   * Delete an S3 object
   * @param s3Key The S3 object key to delete
   * @returns True if successful
   */
  async deleteFile(s3Key: string): Promise<boolean> {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/imageupload?s3Key=${encodeURIComponent(s3Key)}`
      );

      return response.success && response.data?.success === true;
    } catch (error) {
      console.error('Failed to delete S3 object:', error);
      return false;
    }
  }

  /**
   * Upload file directly to S3 using presigned URL with enhanced error handling and retry logic
   * @param presignedUrl The presigned URL for upload
   * @param file The file to upload
   * @param formFields Additional form fields required by S3
   * @param retryCount Current retry attempt (for internal use)
   * @returns Upload result
   */
  private async uploadToS3(
    presignedUrl: string, 
    file: File, 
    formFields: Record<string, string>,
    retryCount: number = 0
  ): Promise<{ success: boolean; error?: string }> {
    const maxRetries = 3;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
    
    try {
      console.log(`🔄 Uploading to S3 (attempt ${retryCount + 1}/${maxRetries + 1}): ${file.name} (${Math.round(file.size / 1024)}KB)`);
      
      // Validate presigned URL before attempting upload
      if (!presignedUrl || presignedUrl.includes('mock-s3')) {
        console.error('❌ Invalid or mock presigned URL detected:', presignedUrl);
        return { 
          success: false, 
          error: 'Invalid S3 configuration - received mock URL. Check backend S3 setup.' 
        };
      }

      // Enhanced upload with timeout and better error handling
      const uploadPromise = this.attemptS3Upload(presignedUrl, file, formFields);
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout')), 120000) // 2 minute timeout
      );

      const response = await Promise.race([uploadPromise, timeoutPromise]);

      if (response.ok) {
        console.log(`✅ S3 upload successful: ${file.name}`);
        return { success: true };
      } else {
        const errorInfo = await this.parseS3Error(response);
        console.error(`❌ S3 upload failed (attempt ${retryCount + 1}): ${response.status} ${response.statusText}`, errorInfo.details);
        
        // Check if this is a retryable error
        const isRetryable = this.isRetryableError(response.status);
        
        if (isRetryable && retryCount < maxRetries) {
          console.log(`🔄 Retrying S3 upload in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return this.uploadToS3(presignedUrl, file, formFields, retryCount + 1);
        }
        
        return { 
          success: false, 
          error: errorInfo.message
        };
      }
    } catch (error: any) {
      console.error(`❌ Network error uploading to S3 (attempt ${retryCount + 1}):`, error);
      
      const errorInfo = this.parseNetworkError(error);
      
      // Check if this is a retryable network error
      const isRetryable = this.isRetryableNetworkError(error);
      
      if (isRetryable && retryCount < maxRetries) {
        console.log(`🔄 Retrying S3 upload after network error in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.uploadToS3(presignedUrl, file, formFields, retryCount + 1);
      }
      
      return { 
        success: false, 
        error: errorInfo
      };
    }
  }

  /**
   * Attempt the actual S3 upload with both PUT and POST methods
   */
  private async attemptS3Upload(
    presignedUrl: string, 
    file: File, 
    formFields: Record<string, string>
  ): Promise<Response> {
    // Try PUT method first (most common for presigned URLs)
    console.log('🔄 Attempting PUT upload to S3...');
    
    let response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
        'Content-Length': file.size.toString()
      }
    });

    // If PUT fails with 405 (Method Not Allowed), try POST with form data
    if (response.status === 405) {
      console.log('🔄 PUT method not allowed, trying POST with form data...');
      
      const formData = new FormData();
      
      // Add form fields first (order matters for S3)
      Object.entries(formFields).forEach(([key, value]) => {
        if (key !== 'x-mock-upload') { // Skip mock flags
          formData.append(key, value);
        }
      });
      
      // Add file last
      formData.append('file', file);
      
      response = await fetch(presignedUrl, {
        method: 'POST',
        body: formData
      });
    }

    return response;
  }

  /**
   * Parse S3 error response for better error messages
   */
  private async parseS3Error(response: Response): Promise<{ message: string; details: string }> {
    let errorText = '';
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = 'Unable to read error response';
    }

    let errorMessage = `S3 upload failed: ${response.status} ${response.statusText}`;
    
    // Provide more specific error messages based on status code
    switch (response.status) {
      case 400:
        errorMessage = 'S3 upload failed: Bad request. Check file format and size.';
        break;
      case 403:
        errorMessage = 'S3 upload failed: Access denied. Check S3 permissions and presigned URL.';
        break;
      case 404:
        errorMessage = 'S3 upload failed: Bucket or path not found.';
        break;
      case 413:
        errorMessage = 'S3 upload failed: File too large for S3 upload.';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorMessage = 'S3 upload failed: Server error. Please try again.';
        break;
      default:
        if (response.status >= 400) {
          errorMessage = `S3 upload failed: ${response.status} ${response.statusText}`;
        }
    }

    return { message: errorMessage, details: errorText };
  }

  /**
   * Parse network error for better error messages
   */
  private parseNetworkError(error: any): string {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      return 'Network error: Unable to connect to S3. Check your internet connection.';
    } else if (error.name === 'AbortError') {
      return 'S3 upload was cancelled or timed out.';
    } else if (error.message?.includes('timeout')) {
      return 'S3 upload timed out. Please try again.';
    } else if (error.message?.includes('CORS')) {
      return 'CORS error: S3 bucket configuration issue. Contact support.';
    } else if (error.message) {
      return `S3 upload error: ${error.message}`;
    } else {
      return 'Unknown network error during S3 upload';
    }
  }

  /**
   * Check if an HTTP status code indicates a retryable error
   */
  private isRetryableError(status: number): boolean {
    // Retry on server errors and some client errors
    return status >= 500 || status === 408 || status === 429;
  }

  /**
   * Check if a network error is retryable
   */
  private isRetryableNetworkError(error: any): boolean {
    // Retry on network timeouts and connection errors
    return error.name === 'TypeError' || 
           error.message?.includes('timeout') ||
           error.message?.includes('network') ||
           error.message?.includes('fetch');
  }

  /**
   * Convert base64 string to File object
   * @param base64String The base64 string (with or without data URL prefix)
   * @param fileName The desired file name
   * @returns File object
   */
  private base64ToFile(base64String: string, fileName: string): File {
    // Remove data URL prefix if present
    const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;
    
    // Determine content type from data URL or default to JPEG
    let contentType = 'image/jpeg';
    if (base64String.startsWith('data:')) {
      const match = base64String.match(/data:([^;]+);/);
      if (match) {
        contentType = match[1];
      }
    }

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create File object
    return new File([byteArray], fileName, { type: contentType });
  }

  /**
   * Check if S3 upload is available (fallback to base64 if not)
   * @returns True if S3 upload should be used
   */
  isS3UploadAvailable(): boolean {
    // In development, you might want to disable S3 upload
    // In production, this should always return true
    return true;
  }
}

// Create a default instance
export const s3UploadService = new S3UploadService();

export default s3UploadService;
